import styled, { css } from 'styled-components';
import { ReactElement } from 'react';

import { Banner } from '@/ui/Banner';
import { QuizResultsLabel } from '@/administration/pages/Course/pages/quiz/View/ViewQuizPage/QuizResultsLabel';

const RootStyled = styled.div<{ hasSubmitted: boolean; hasPassingCriteria: boolean }>`
  display: flex;
  gap: 8px;
  width: 100%;

  ${({ hasSubmitted, hasPassingCriteria }) =>
    !hasSubmitted || !hasPassingCriteria
      ? css`
          margin-bottom: -64px;
        `
      : css`
          margin-bottom: -48px;
        `}

  margin-left: 58px;
  margin-right: 58px;
  margin-top: 72px;

  @media (min-width: ${(p) => p.theme.breakpoints.desktopLarge.end}px) {
    margin-left: 0;
    margin-right: 0;
  }
`;

type QuizBannerElementProps = {
  hasPassingCriteria: boolean;
  hasSubmitted: boolean;
  hasPassed: boolean;
  correctAnswersCount?: number;
  totalQuestionsCount: number;
  passingCriteria: number;
  score?: number;
};

export const QuizBannerElement = ({
  hasPassingCriteria,
  hasSubmitted,
  hasPassed,
  correctAnswersCount,
  passingCriteria,
  totalQuestionsCount,
  score,
}: QuizBannerElementProps): ReactElement => {
  const requiredCorrectAnswersCount = Math.ceil((passingCriteria / 100) * totalQuestionsCount);

  const bannerLabel = hasPassingCriteria
    ? `Passing criteria: ${passingCriteria}% (${requiredCorrectAnswersCount} out of ${totalQuestionsCount} correct answers) to complete this quiz`
    : 'No passing criteria';

  const postSubmissionPassingCriteriaMessage = hasPassed
    ? `You passed the quiz with ${correctAnswersCount} out of ${totalQuestionsCount} correct answers`
    : `You didn't pass the quiz. You need ${requiredCorrectAnswersCount} out of ${totalQuestionsCount} correct answers to pass this quiz`;

  return (
    <RootStyled hasSubmitted={hasSubmitted} hasPassingCriteria={hasPassingCriteria}>
      {hasSubmitted ? (
        <QuizResultsLabel
          hasPassingCriteria={hasPassingCriteria}
          passingCriteriaMessage={postSubmissionPassingCriteriaMessage}
          hasPassed={hasPassed}
          correctAnswersCount={correctAnswersCount}
          allQuestionsCount={totalQuestionsCount}
          score={score}
        />
      ) : (
        <Banner label={bannerLabel} variant="light" icon="info-circle" withRoundedBorder />
      )}
    </RootStyled>
  );
};
